import { Alert, Card, Descriptions, Space, Table, Tag } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import api from '@/services/api.service';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';
import PortalHomeworkCard, { type PortalHomework } from '@/pages/education/components/PortalHomeworkCard';
import PortalAnnouncementsCard, {
  type PortalAnnouncements,
} from '@/pages/education/components/PortalAnnouncementsCard';

type Home = {
  student: {
    admission_no: string;
    first_name: string;
    last_name?: string | null;
    status: string;
    campus_name?: string | null;
  } | null;
  enrollment?: { grade_name?: string; section_name?: string; session_name?: string; status?: string } | null;
  attendance?: { total_marks: number; by_status: Record<string, number>; window_days: number };
  fees?: { outstanding_amount: number; open_demands: number };
  fee_statement?: {
    read_only: true;
    lines: Array<{
      id: string;
      period_key: string;
      status: string;
      amount: number;
      currency_code: string;
      due_on: string | null;
    }>;
  };
  published_results?: {
    advisory: string;
    entries: Array<{ component_code: string; marks_raw: number | null; published_at: string | null }>;
  };
  homework?: PortalHomework;
  announcements?: PortalAnnouncements;
  message?: string;
};

export default function StudentPortalPage() {
  const { t } = useTranslation('education');
  const query = useQuery({
    queryKey: ['education-portal-student'],
    queryFn: async () => (await api.get<Home>('/education/portal/student')).data,
  });
  const home = query.data;

  return (
    <EducationPageChrome title={t('studentPortal')} description={t('studentPortalIntro')}>
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Alert type="info" showIcon message={t('portalGrantedOnlyHint')} />
        {home?.message ? <Alert type="warning" showIcon message={home.message} /> : null}
        <Card size="small" loading={query.isLoading}>
          {home?.student ? (
            <Descriptions column={{ xs: 1, md: 2 }} size="small">
              <Descriptions.Item label={t('admissionNo')}>{home.student.admission_no}</Descriptions.Item>
              <Descriptions.Item label={t('student')}>
                {[home.student.first_name, home.student.last_name].filter(Boolean).join(' ')}
              </Descriptions.Item>
              <Descriptions.Item label={t('status')}>
                <Tag>{home.student.status}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label={t('campus')}>{home.student.campus_name || '—'}</Descriptions.Item>
              <Descriptions.Item label={t('grade')}>
                {[home.enrollment?.grade_name, home.enrollment?.section_name].filter(Boolean).join(' / ') ||
                  '—'}
              </Descriptions.Item>
              <Descriptions.Item label={t('session')}>
                {home.enrollment?.session_name || '—'}
              </Descriptions.Item>
              <Descriptions.Item label={t('attendance')}>
                {home.attendance
                  ? t('portalAttendanceSummary', {
                      total: home.attendance.total_marks,
                      days: home.attendance.window_days,
                    })
                  : '—'}
              </Descriptions.Item>
              <Descriptions.Item label={t('feesOutstanding')}>
                {home.fees?.outstanding_amount ?? 0} ({home.fees?.open_demands ?? 0} {t('demands')})
              </Descriptions.Item>
            </Descriptions>
          ) : null}
        </Card>
        <Card
          size="small"
          title={t('portalAttendanceBreakdown', { days: home?.attendance?.window_days ?? 14 })}
          loading={query.isLoading}
        >
          {home?.attendance && home.attendance.total_marks > 0 ? (
            <Space wrap>
              {Object.entries(home.attendance.by_status).map(([code, count]) => (
                <Tag key={code}>
                  {code}: {count}
                </Tag>
              ))}
            </Space>
          ) : (
            '—'
          )}
        </Card>
        <PortalAnnouncementsCard announcements={home?.announcements} loading={query.isLoading} />
        <PortalHomeworkCard homework={home?.homework} loading={query.isLoading} />
        <Card size="small" title={t('portalFeeStatement')} loading={query.isLoading}>
          <Alert type="info" showIcon style={{ marginBottom: 12 }} message={t('portalFeeStatementHint')} />
          <Table
            rowKey="id"
            size="small"
            pagination={false}
            locale={{ emptyText: t('portalNoFeeLines') }}
            dataSource={home?.fee_statement?.lines ?? []}
            columns={[
              { title: t('portalPeriodKey'), dataIndex: 'period_key' },
              { title: t('status'), dataIndex: 'status', render: (v: string) => <Tag>{v}</Tag> },
              { title: t('amount'), dataIndex: 'amount', align: 'right' as const },
              { title: t('portalCurrency'), dataIndex: 'currency_code', width: 90 },
              { title: t('dueOn'), dataIndex: 'due_on', render: (v: string | null) => v || '—' },
            ]}
          />
        </Card>
        <Card size="small" title={t('portalPublishedResults')} loading={query.isLoading}>
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            message={t('portalPublishedResultsHint')}
          />
          <Table
            rowKey={(r) => `${r.component_code}-${r.published_at ?? ''}`}
            size="small"
            pagination={false}
            locale={{ emptyText: t('portalNoPublishedResults') }}
            dataSource={home?.published_results?.entries ?? []}
            columns={[
              { title: t('component'), dataIndex: 'component_code' },
              {
                title: t('portalMarks'),
                dataIndex: 'marks_raw',
                align: 'right' as const,
                render: (v: number | null) => (v == null ? '—' : v),
              },
              {
                title: t('portalPublishedAt'),
                dataIndex: 'published_at',
                render: (v: string | null) => (v ? v.slice(0, 10) : '—'),
              },
            ]}
          />
        </Card>
      </Space>
    </EducationPageChrome>
  );
}
